import { Injectable, Inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';

/** Flat config object accepted by updateAll() — mirrors route data keys */
export interface SeoConfig {
  title?: string;
  description?: string;
  keywords?: string;
  /** Canonical URL — also used as og:url */
  canonical?: string;
  /** Alias for canonical (matches existing route data key "url") */
  url?: string;
  /** og:image / twitter:image */
  ogImage?: string;
  /** Alias for ogImage (matches existing route data key "image") */
  image?: string;
  /** og:type — defaults to "website" */
  ogType?: string;
  /**
   * <meta name="robots"> content, e.g. "noindex, follow".
   * Leave undefined on indexable pages — updateAll() then REMOVES any robots
   * meta left behind by a previous client-side navigation, so a noindex page
   * can never leak its directive onto the next page the user visits.
   */
  robots?: string;
  /** og:title / twitter:title — falls back to title when not supplied */
  ogTitle?: string;
  /** og:description / twitter:description — falls back to description when not supplied */
  ogDescription?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  constructor(
    private titleService: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private doc: Document
  ) {}

  /** Set <title> */
  updateTitle(title: string): void {
    if (title) this.titleService.setTitle(title);
  }

  /** Set <meta name="description"> */
  updateDescription(description: string): void {
    if (description) this.meta.updateTag({ name: 'description', content: description });
  }

  /**
   * Remove any <meta name="keywords"> tag. The keywords meta is deprecated,
   * ignored by Google/Bing, and was flagged in the SEO audit — we no longer emit it.
   */
  removeMetaKeywords(): void {
    this.meta.removeTag('name="keywords"');
  }

  /**
   * @deprecated Keywords meta is no longer emitted. Retained so existing callers
   * keep compiling; it now strips any keywords tag instead of adding one.
   */
  updateMetaKeywords(_keywords?: string): void {
    this.removeMetaKeywords();
  }

  /**
   * Set or clear <meta name="robots">.
   *
   * Passing a value writes the directive into the rendered HTML, which is what
   * makes a page's noindex survive header stripping at a CDN and what the
   * prerender step bakes into the static file. Passing nothing removes the tag,
   * which matters in the SPA: without the removal, navigating /csat -> /careers
   * would carry the noindex onto an indexable page.
   */
  updateRobots(robots?: string): void {
    if (robots) {
      this.meta.updateTag({ name: 'robots', content: robots });
    } else {
      this.meta.removeTag('name="robots"');
    }
  }

  /** Inject or update <link rel="canonical"> — SSR-safe via DOCUMENT */
  updateCanonical(url: string): void {
    if (!url) return;
    let link = this.doc.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  /** Set og:* Open Graph meta tags */
  updateOgTags(config: SeoConfig): void {
    const canonicalUrl = config.canonical || config.url || '';
    const image = config.ogImage || config.image || '';
    const ogTitle = config.ogTitle || config.title;
    const ogDescription = config.ogDescription || config.description;

    if (ogTitle) this.meta.updateTag({ property: 'og:title', content: ogTitle });
    if (ogDescription) this.meta.updateTag({ property: 'og:description', content: ogDescription });
    if (image) this.meta.updateTag({ property: 'og:image', content: image });
    if (canonicalUrl) this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
    this.meta.updateTag({ property: 'og:type', content: config.ogType || 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'Kalpita Technologies' });
  }

  /** Set twitter:* card meta tags */
  updateTwitterTags(config: SeoConfig): void {
    const image = config.ogImage || config.image || '';
    const ogTitle = config.ogTitle || config.title;
    const ogDescription = config.ogDescription || config.description;
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:site', content: '@KalpitaTech' });
    if (ogTitle) this.meta.updateTag({ name: 'twitter:title', content: ogTitle });
    if (ogDescription) this.meta.updateTag({ name: 'twitter:description', content: ogDescription });
    if (image) this.meta.updateTag({ name: 'twitter:image', content: image });
  }

  /**
   * One-shot SSR-safe SEO update — call this in ngOnInit() with ActivatedRoute.snapshot.data.
   * Handles title, description, keywords, canonical, og:*, twitter:*
   */
  updateAll(data: SeoConfig): void {
    const canonicalUrl = data.canonical || data.url || '';
    const ogImage =
      data.ogImage || data.image ||
      'https://www.kalpitatechnologies.com/assets/common/black-logo.png';

    if (data.title) this.updateTitle(data.title);
    if (data.description) this.updateDescription(data.description);
    this.removeMetaKeywords(); // never emit the deprecated keywords meta
    this.updateRobots(data.robots); // set on noindex routes, cleared everywhere else
    if (canonicalUrl) this.updateCanonical(canonicalUrl);
    this.updateOgTags({ ...data, image: ogImage, url: canonicalUrl });
    this.updateTwitterTags({ ...data, image: ogImage });
  }
  apply(config: SeoConfig): void {
    this.updateAll(config);
  }
}